import { useCallback, useContext } from 'react';

import { Workflows } from './tracking-constants';
import { getWorkflowState } from '../fulfillment-planner/hooks/use-workflow-guids-state';
import { Segment } from '../services/analytics-services/segment';
import { SegmentEventWorkflowTrackingContext } from '../services/analytics-services/segment-event-workflow-tracking-context';
import { Yofi } from '../services/analytics-services/yofi';
import Global from '../store/global-selectors';

export const WorkflowSteps = {
  START: 'START',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR',
  CANCEL: 'CANCEL',
  CLICK: 'CLICK',
  REDIRECT: 'REDIRECT',
  CONTINUE: 'CONTINUE',
  CLOSE: 'CLOSE',
  INTERACTION: 'INTERACTION',
  // Carrier Connection (WWEX registration)
  CARRIER_CONNECTION_CHOOSER_SHOWN: 'CARRIER_CONNECTION_CHOOSER_SHOWN',
  CARRIER_CONNECTION_FLOW_CHOSEN: 'CARRIER_CONNECTION_FLOW_CHOSEN',
  CARRIER_CONNECTION_MODAL_OPENED: 'CARRIER_CONNECTION_MODAL_OPENED',
  CARRIER_CONNECTION_PAYMENT_METHOD_SELECTED: 'CARRIER_CONNECTION_PAYMENT_METHOD_SELECTED',
  CARRIER_CONNECTION_SUBMITTED: 'CARRIER_CONNECTION_SUBMITTED',
  // Order Import
  ORDER_IMPORT_SELECT_FILE_CLICK: 'ORDER_IMPORT_SELECT_FILE_CLICK',
  ORDER_IMPORT_DOWNLOAD_SAMPLE_CLICK: 'ORDER_IMPORT_DOWNLOAD_SAMPLE_CLICK',
  ORDER_IMPORT_UPLOAD_CLICK: 'ORDER_IMPORT_UPLOAD_CLICK',
  ORDER_IMPORT_BACK_CLICK: 'ORDER_IMPORT_BACK_CLICK',
  ORDER_IMPORT_MAPPING_CLICK: 'ORDER_IMPORT_DROPDOWN_CLICK',
  ORDER_IMPORT_MAPPING_SELECTION: 'ORDER_IMPORT_MAPPING_SELECTION',
  ORDER_IMPORT_DOWNLOAD_SAMPLE_ERROR: 'ORDER_IMPORT_DOWNLOAD_SAMPLE_ERROR',
  ORDER_IMPORT_FILE_SELECTED: 'ORDER_IMPORT_FILE_SELECTED',
  ORDER_IMPORT_START_IMPORT: 'ORDER_IMPORT_START_IMPORT',

  CSV_IMPORT_DOWNLOAD_SAMPLE: 'CSV_IMPORT_DOWNLOAD_SAMPLE',
  CSV_IMPORT_UPLOAD_FILE: 'CSV_IMPORT_UPLOAD_FILE',
  CSV_IMPORT_CHANGE_CUSTOM_PROPERTY: 'CSV_IMPORT_CHANGE_CUSTOM_PROPERTY',
  CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE: 'CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE',
  CSV_IMPORT_CHANGE_MAPPING_FIELD: 'CSV_IMPORT_CHANGE_MAPPING_FIELD',
  CSV_IMPORT_INIT_IMPORT: 'CSV_IMPORT_INIT_IMPORT',
  CSV_IMPORT_INIT_IMPORT_ERROR: 'CSV_IMPORT_INIT_IMPORT_ERROR',
  CSV_IMPORT_START_IMPORT: 'CSV_IMPORT_START_IMPORT',
  CSV_IMPORT_START_IMPORT_ERROR: 'CSV_IMPORT_START_IMPORT_ERROR',
  CSV_IMPORT_GET_SUMMARY: 'CSV_IMPORT_GET_SUMMARY',
  CSV_IMPORT_GET_SUMMARY_ERROR: 'CSV_IMPORT_GET_SUMMARY_ERROR',
  // Rate Browser
  RATE_BROWSER_ALL_RATES_RETURNED: 'RATE_BROWSER_ALL_RATES_RETURNED',
  RATE_BROWSER_ALL_RATES_ERROR_BANNER_SHOWN: 'RATE_BROWSER_ALL_RATES_ERROR_BANNER_SHOWN',
  RATE_BROWSER_BROWSE_RATES_CLICKED: 'RATE_BROWSER_BROWSE_RATES_CLICKED',
  RATE_BROWSER_BROWSE_FREIGHT_RATES_CLICKED: 'RATE_BROWSER_BROWSE_FREIGHT_RATES_CLICKED',
  RATE_BROWSER_CHANGE_VIEW: 'RATE_BROWSER_CHANGE_VIEW',
  RATE_BROWSER_ALL_RATES_ERROR_BANNER_EXPANDED: 'RATE_BROWSER_ALL_RATES_ERROR_BANNER_EXPANDED',
  RATE_BROWSER_ALL_RATES_ERROR_BANNER_DISMISSED: 'RATE_BROWSER_ALL_RATES_ERROR_BANNER_DISMISSED',
  RATE_BROWSER_CONFIGURE_LABEL: 'RATE_BROWSER_CONFIGURE_LABEL',
  RATE_BROWSER_CREATE_LABEL_CLICKED: 'RATE_BROWSER_CREATE_LABEL_CLICKED',
  // Old Rate Browser
  RATE_BROWSER_PROVIDER_CLICKED: 'RATE_BROWSER_PROVIDER_CLICKED',
  RATE_BROWSER_PROVIDER_RATE_CLICKED: 'RATE_BROWSER_PROVIDER_RATE_CLICKED',

  SAVE_ASN_STEP: 'SAVE_ASN_STEP',
  SUBMIT_FREIGHT_CONFIGURATION: 'SUBMIT_FREIGHT_CONFIGURATION',

  // Scan Page
  REPORT_ISSUE: 'REPORT_ISSUE',
  ITEM_VERIFIED_MANUAL: 'ITEM_VERIFIED_MANUAL',
  ITEM_VERIFIED_SCANNED: 'ITEM_VERIFIED_SCANNED',
  EDIT_VERIFICATION: 'EDIT_VERIFICATION',
  VERIFY_ALL: 'VERIFY_ALL',
  CLEAR_ALL: 'CLEAR_ALL',

  // Scan To Receive
  NOT_FOUND: 'NOT_FOUND',
  IS_BUNDLE: 'IS_BUNDLE',

  // FTU Wizard
  FTU_WIZARD_SKIP: 'FTU_WIZARD_SKIP',
  FTU_WIZARD_BACK: 'FTU_WIZARD_BACK',

  // Create Return
  ITEMS_SELECTED: 'ITEMS_SELECTED',

  // Store Error Resolution
  CONTACT_SUPPORT: 'CONTACT_SUPPORT',
  GET_HELP: 'GET_HELP',

  // Activate Enhanced Returns
  SELECT_STORE: 'SELECT_STORE',
  REAUTHENTICATE_STORE: 'REAUTHENTICATE_STORE',
  SELECT_CARRIER: 'SELECT_CARRIER',
  SELECT_RETURN_LOCATION: 'SELECT_RETURN_LOCATION',

  // Promotional Plan Migration
  ADD_PAYMENT_CLICKED: 'ADD_PAYMENT_CLICKED',
  ADD_PAYMENT_COMPLETE: 'ADD_PAYMENT_COMPLETE',

  // Harmonization specific steps
  HARMONIZATION_CODE_SELECTED: 'HARMONIZATION_CODE_SELECTED',
  HARMONIZATION_ACCEPT_HIGH_CONFIDENCE: 'HARMONIZATION_ACCEPT_HIGH_CONFIDENCE',

  // HTML Editor specific steps
  CHANGE_SETTING: 'CHANGE_SETTING',
  INSERT_FIELD: 'INSERT_FIELD',
  FORMAT_HTML: 'FORMAT_HTML',
  UNDO: 'UNDO',
  REDO: 'REDO',
  REFRESH_PREVIEW: 'REFRESH_PREVIEW',
  ZOOM_IN: 'ZOOM_IN',
  ZOOM_OUT: 'ZOOM_OUT',
  OPEN_CHAT_ASSISTANT: 'OPEN_CHAT_ASSISTANT',
};

const EventsThatUpdateWorkflowData = [
  WorkflowSteps.START,
  WorkflowSteps.CONTINUE,
  WorkflowSteps.CARRIER_CONNECTION_FLOW_CHOSEN,
  WorkflowSteps.EDIT_VERIFICATION,
  WorkflowSteps.CLEAR_ALL,
  WorkflowSteps.VERIFY_ALL,
  WorkflowSteps.ITEM_VERIFIED_MANUAL,
  WorkflowSteps.ITEM_VERIFIED_SCANNED,
];

export const WorkFlowStepToSegmentEventMapping = {
  [Workflows.ACTIVATE_ENHANCED_RETURNS_FLOW]: {
    [WorkflowSteps.START]: 'ACTIVATE_ENHANCED_RETURNS_START',
    [WorkflowSteps.SELECT_STORE]: 'ACTIVATE_ENHANCED_RETURNS_SELECT_STORE',
    [WorkflowSteps.REAUTHENTICATE_STORE]: 'ACTIVATE_ENHANCED_RETURNS_REAUTHENTICATE_STORE',
    [WorkflowSteps.SELECT_CARRIER]: 'ACTIVATE_ENHANCED_RETURNS_SELECT_CARRIER',
    [WorkflowSteps.SELECT_RETURN_LOCATION]: 'ACTIVATE_ENHANCED_RETURNS_SELECT_RETURN_LOCATION',
    [WorkflowSteps.COMPLETE]: 'ACTIVATE_ENHANCED_RETURNS_COMPLETE',
    [WorkflowSteps.ERROR]: 'ACTIVATE_ENHANCED_RETURNS_ERROR',
    [WorkflowSteps.CANCEL]: 'ACTIVATE_ENHANCED_RETURNS_CANCEL',
  },
  [Workflows.ADD_FUNDS_FLOW]: {
    [WorkflowSteps.START]: 'ADD_FUNDS_START',
    [WorkflowSteps.COMPLETE]: 'ADD_FUNDS_COMPLETE',
    [WorkflowSteps.ERROR]: 'ADD_FUNDS_ERROR',
    [WorkflowSteps.CANCEL]: 'ADD_FUNDS_CANCEL',
  },
  [Workflows.WALLET_REGISTRATION_FLOW]: {
    [WorkflowSteps.START]: 'WALLET_REGISTRATION_START',
    [WorkflowSteps.COMPLETE]: 'WALLET_REGISTRATION_COMPLETE',
    [WorkflowSteps.ERROR]: 'WALLET_REGISTRATION_ERROR',
    [WorkflowSteps.CANCEL]: 'WALLET_REGISTRATION_CANCEL',
  },
  [Workflows.ORDER_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'ORDER_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'ORDER_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'ORDER_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'ORDER_IMPORT_CANCEL',
    [WorkflowSteps.ORDER_IMPORT_SELECT_FILE_CLICK]: 'ORDER_IMPORT_SELECT_FILE_CLICK',
    [WorkflowSteps.ORDER_IMPORT_DOWNLOAD_SAMPLE_CLICK]: 'ORDER_IMPORT_DOWNLOAD_SAMPLE_CLICK',
    [WorkflowSteps.ORDER_IMPORT_UPLOAD_CLICK]: 'ORDER_IMPORT_UPLOAD_CLICK',
    [WorkflowSteps.ORDER_IMPORT_BACK_CLICK]: 'ORDER_IMPORT_BACK_CLICK',
    [WorkflowSteps.ORDER_IMPORT_MAPPING_CLICK]: 'ORDER_IMPORT_MAPPING_CLICK',
    [WorkflowSteps.ORDER_IMPORT_MAPPING_SELECTION]: 'ORDER_IMPORT_MAPPING_SELECTION',
    [WorkflowSteps.ORDER_IMPORT_DOWNLOAD_SAMPLE_ERROR]: 'ORDER_IMPORT_DOWNLOAD_SAMPLE_ERROR',
    [WorkflowSteps.ORDER_IMPORT_FILE_SELECTED]: 'ORDER_IMPORT_FILE_SELECTED',
    [WorkflowSteps.ORDER_IMPORT_START_IMPORT]: 'ORDER_IMPORT_START_IMPORT',
  },
  [Workflows.PRODUCT_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'PRODUCT_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'PRODUCT_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'PRODUCT_IMPORT_ERROR',
  },
  [Workflows.INVENTORY_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'INVENTORY_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'INVENTORY_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'INVENTORY_IMPORT_ERROR',
  },
  [Workflows.MARKETPLACE_CONNECTION_FLOW]: {
    [WorkflowSteps.START]: 'MARKETPLACE_CONNECTION_START',
    [WorkflowSteps.COMPLETE]: 'MARKETPLACE_CONNECTION_COMPLETE',
    [WorkflowSteps.ERROR]: 'MARKETPLACE_CONNECTION_ERROR',
    [WorkflowSteps.CANCEL]: 'MARKETPLACE_CONNECTION_CANCEL',
    [WorkflowSteps.CLICK]: 'MARKETPLACE_CONNECTION_TILE_CLICKED',
    [WorkflowSteps.REDIRECT]: 'MARKETPLACE_CONNECTION_REDIRECT',
  },
  [Workflows.CREATE_AUTOMATION_RULES_FLOW]: {
    [WorkflowSteps.START]: 'CREATE_AUTOMATION_RULE_START',
    [WorkflowSteps.COMPLETE]: 'CREATE_AUTOMATION_RULE_COMPLETE',
    [WorkflowSteps.ERROR]: 'CREATE_AUTOMATION_RULE_ERROR',
    [WorkflowSteps.CANCEL]: 'CREATE_AUTOMATION_RULE_CANCEL',
  },
  [Workflows.SIMPLE_LABEL_FLOW]: {
    [WorkflowSteps.START]: 'SIMPLE_LABEL_FLOW_START',
    [WorkflowSteps.CLICK]: 'SIMPLE_LABEL_FLOW_CLICK',
    [WorkflowSteps.COMPLETE]: 'SIMPLE_LABEL_FLOW_COMPLETE',
    [WorkflowSteps.ERROR]: 'SIMPLE_LABEL_FLOW_ERROR',
    [WorkflowSteps.CANCEL]: 'SIMPLE_LABEL_FLOW_CANCEL',
  },
  [Workflows.CARRIER_CONNECTION_FLOW]: {
    [WorkflowSteps.START]: 'CARRIER_CONNECTION_START',
    [WorkflowSteps.COMPLETE]: 'CARRIER_CONNECTION_COMPLETE',
    [WorkflowSteps.ERROR]: 'CARRIER_CONNECTION_ERROR',
    [WorkflowSteps.CANCEL]: 'CARRIER_CONNECTION_CANCEL',
    [WorkflowSteps.CARRIER_CONNECTION_CHOOSER_SHOWN]: 'CARRIER_CONNECTION_CHOOSER_SHOWN',
    [WorkflowSteps.CARRIER_CONNECTION_FLOW_CHOSEN]: 'CARRIER_CONNECTION_FLOW_CHOSEN',
    [WorkflowSteps.CARRIER_CONNECTION_MODAL_OPENED]: 'CARRIER_CONNECTION_MODAL_OPENED',
    [WorkflowSteps.CARRIER_CONNECTION_PAYMENT_METHOD_SELECTED]: 'CARRIER_CONNECTION_PAYMENT_METHOD_SELECTED',
    [WorkflowSteps.CARRIER_CONNECTION_SUBMITTED]: 'CARRIER_CONNECTION_SUBMITTED',
    [WorkflowSteps.INTERACTION]: 'CARRIER_CONNECTION_INTERACTION',
  },
  [Workflows.BILLING_INFO_FLOW]: {
    [WorkflowSteps.START]: 'BILLING_INFO_START',
    [WorkflowSteps.COMPLETE]: 'BILLING_INFO_COMPLETE',
    [WorkflowSteps.ERROR]: 'BILLING_INFO_ERROR',
    [WorkflowSteps.CANCEL]: 'BILLING_INFO_CANCEL',
    [WorkflowSteps.CLICK]: 'BILLING_INFO_CHECKOUT_WITH_PAYPAL_CLICK',
  },
  [Workflows.ADD_ON_FLOW]: {
    [WorkflowSteps.START]: 'ADD_ON_FLOW_START',
    [WorkflowSteps.CANCEL]: 'ADD_ON_FLOW_CANCEL',
    [WorkflowSteps.COMPLETE]: 'ADD_ON_FLOW_COMPLETE',
    [WorkflowSteps.ERROR]: 'ADD_ON_FLOW_ERROR',
  },
  [Workflows.RATE_BROWSER_FLOW]: {
    [WorkflowSteps.START]: 'RATE_BROWSER_FLOW_START',
    [WorkflowSteps.CANCEL]: 'RATE_BROWSER_FLOW_CANCEL',
    [WorkflowSteps.COMPLETE]: 'RATE_BROWSER_FLOW_COMPLETE',
    [WorkflowSteps.ERROR]: 'RATE_BROWSER_FLOW_ERROR',
    [WorkflowSteps.CLICK]: 'RATE_BROWSER_FLOW_CLICK',
    [WorkflowSteps.RATE_BROWSER_BROWSE_RATES_CLICKED]: 'RATE_BROWSER_BROWSE_RATES_CLICKED',
    [WorkflowSteps.RATE_BROWSER_BROWSE_FREIGHT_RATES_CLICKED]: 'RATE_BROWSER_BROWSE_FREIGHT_RATES_CLICKED',
    [WorkflowSteps.RATE_BROWSER_ALL_RATES_RETURNED]: 'RATE_BROWSER_ALL_RATES_RETURNED',
    [WorkflowSteps.RATE_BROWSER_ALL_RATES_ERROR_BANNER_SHOWN]: 'RATE_BROWSER_ALL_RATES_ERROR_BANNER_SHOWN',
    [WorkflowSteps.RATE_BROWSER_CHANGE_VIEW]: 'RATE_BROWSER_CHANGE_VIEW',
    [WorkflowSteps.RATE_BROWSER_ALL_RATES_ERROR_BANNER_EXPANDED]: 'RATE_BROWSER_ALL_RATES_ERROR_BANNER_EXPANDED',
    [WorkflowSteps.RATE_BROWSER_ALL_RATES_ERROR_BANNER_DISMISSED]: 'RATE_BROWSER_ALL_RATES_ERROR_BANNER_DISMISSED',
    [WorkflowSteps.RATE_BROWSER_CONFIGURE_LABEL]: 'RATE_BROWSER_CONFIGURE_LABEL',
    [WorkflowSteps.RATE_BROWSER_CREATE_LABEL_CLICKED]: 'RATE_BROWSER_CREATE_LABEL_CLICKED',
    [WorkflowSteps.RATE_BROWSER_PROVIDER_CLICKED]: 'RATE_BROWSER_PROVIDER_CLICKED',
    [WorkflowSteps.RATE_BROWSER_PROVIDER_RATE_CLICKED]: 'RATE_BROWSER_PROVIDER_RATE_CLICKED',
  },
  [Workflows.CSV_PRODUCT_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_PRODUCT_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_PRODUCT_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_PRODUCT_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_PRODUCT_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_PRODUCT_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_PRODUCT_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_PRODUCT_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_PRODUCT_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_PRODUCT_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_PRODUCT_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_PRODUCT_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_PRODUCT_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_PRODUCT_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_PRODUCT_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_PRODUCT_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_DANGEROUS_GOODS_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_DANGEROUS_GOODS_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_DANGEROUS_GOODS_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_DANGEROUS_GOODS_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_DANGEROUS_GOODS_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_DANGEROUS_GOODS_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_DANGEROUS_GOODS_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_DANGEROUS_GOODS_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_DANGEROUS_GOODS_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_DANGEROUS_GOODS_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_DANGEROUS_GOODS_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_DANGEROUS_GOODS_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_DANGEROUS_GOODS_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_DANGEROUS_GOODS_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_DANGEROUS_GOODS_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_DANGEROUS_GOODS_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_PRODUCT_UOM_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_PRODUCT_UOM_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_PRODUCT_UOM_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_PRODUCT_UOM_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_PRODUCT_UOM_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_PRODUCT_UOM_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_PRODUCT_UOM_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_PRODUCT_UOM_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_PRODUCT_UOM_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_PRODUCT_UOM_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_PRODUCT_UOM_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_PRODUCT_UOM_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_PRODUCT_UOM_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_PRODUCT_UOM_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_PRODUCT_UOM_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_PRODUCT_UOM_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_FREIGHT_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_FREIGHT_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_FREIGHT_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_FREIGHT_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_FREIGHT_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_FREIGHT_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_FREIGHT_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_FREIGHT_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_FREIGHT_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_FREIGHT_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_FREIGHT_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_FREIGHT_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_FREIGHT_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_FREIGHT_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_FREIGHT_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_FREIGHT_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_FREIGHT_COMMODITY_PRESET_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_FREIGHT_COMMODITY_PRESET_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_FREIGHT_HANDLING_UNIT_PRESET_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_CUSTOM_PACKAGE_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_CUSTOM_PACKAGE_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_CUSTOM_PACKAGE_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_CUSTOM_PACKAGE_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_CUSTOM_PACKAGE_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_CUSTOM_PACKAGE_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_CUSTOM_PACKAGE_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_CUSTOM_PACKAGE_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_CUSTOM_PACKAGE_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_CUSTOM_PACKAGE_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_CUSTOM_PACKAGE_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_CUSTOM_PACKAGE_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_CUSTOM_PACKAGE_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_CUSTOM_PACKAGE_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_CUSTOM_PACKAGE_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_CUSTOM_PACKAGE_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_INVENTORY_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_INVENTORY_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_INVENTORY_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_INVENTORY_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_INVENTORY_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_INVENTORY_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_INVENTORY_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_INVENTORY_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_INVENTORY_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_INVENTORY_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_INVENTORY_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_INVENTORY_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_INVENTORY_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_INVENTORY_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_INVENTORY_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_INVENTORY_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_ORDER_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_ORDER_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_ORDER_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_ORDER_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_ORDER_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_ORDER_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_ORDER_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_ORDER_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_ORDER_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_ORDER_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_ORDER_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_ORDER_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_ORDER_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_ORDER_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_ORDER_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_ORDER_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_FTU_PRODUCT_WITH_INVENTORY_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_INVENTORY_LOCATION_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_INVENTORY_LOCATION_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_INVENTORY_LOCATION_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_INVENTORY_LOCATION_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_INVENTORY_LOCATION_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_INVENTORY_LOCATION_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_INVENTORY_LOCATION_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_INVENTORY_LOCATION_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_INVENTORY_LOCATION_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_INVENTORY_LOCATION_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_INVENTORY_LOCATION_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_INVENTORY_LOCATION_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_INVENTORY_LOCATION_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_INVENTORY_LOCATION_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_INVENTORY_LOCATION_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_INVENTORY_LOCATION_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_PURCHASE_ORDER_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_PURCHASE_ORDER_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_PURCHASE_ORDER_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_PURCHASE_ORDER_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_PURCHASE_ORDER_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_PURCHASE_ORDER_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_PURCHASE_ORDER_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_PURCHASE_ORDER_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_PURCHASE_ORDER_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_PURCHASE_ORDER_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_PURCHASE_ORDER_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_PURCHASE_ORDER_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_PURCHASE_ORDER_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_PURCHASE_ORDER_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_PURCHASE_ORDER_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_PURCHASE_ORDER_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_TOTE_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_TOTE_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_TOTE_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_TOTE_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_TOTE_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_TOTE_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_TOTE_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_TOTE_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_TOTE_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_TOTE_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_TOTE_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_TOTE_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_TOTE_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_TOTE_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_TOTE_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_TOTE_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_ASN_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_ASN_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_ASN_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_ASN_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_ASN_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_ASN_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_ASN_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_ASN_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_ASN_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_ASN_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_ASN_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_ASN_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_ASN_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_ASN_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_ASN_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_ASN_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_BLACKLIST_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_BLACKLIST_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_BLACKLIST_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_BLACKLIST_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_BLACKLIST_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_BLACKLIST_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_BLACKLIST_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_BLACKLIST_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_BLACKLIST_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_BLACKLIST_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_BLACKLIST_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_BLACKLIST_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_BLACKLIST_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_BLACKLIST_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_BLACKLIST_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_BLACKLIST_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.CSV_SUPPLIER_IMPORT_FLOW]: {
    [WorkflowSteps.START]: 'CSV_SUPPLIER_IMPORT_START',
    [WorkflowSteps.COMPLETE]: 'CSV_SUPPLIER_IMPORT_COMPLETE',
    [WorkflowSteps.ERROR]: 'CSV_SUPPLIER_IMPORT_ERROR',
    [WorkflowSteps.CANCEL]: 'CSV_SUPPLIER_IMPORT_CANCEL',
    [WorkflowSteps.CSV_IMPORT_DOWNLOAD_SAMPLE]: 'CSV_SUPPLIER_IMPORT_DOWNLOAD_SAMPLE',
    [WorkflowSteps.CSV_IMPORT_UPLOAD_FILE]: 'CSV_SUPPLIER_IMPORT_UPLOAD_FILE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_CUSTOM_PROPERTY]: 'CSV_SUPPLIER_IMPORT_CHANGE_CUSTOM_PROPERTY',
    [WorkflowSteps.CSV_IMPORT_CHOOSE_MAPPING_TEMPLATE]: 'CSV_SUPPLIER_IMPORT_CHOOSE_MAPPING_TEMPLATE',
    [WorkflowSteps.CSV_IMPORT_CHANGE_MAPPING_FIELD]: 'CSV_SUPPLIER_IMPORT_CHANGE_MAPPING_FIELD',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT]: 'CSV_SUPPLIER_IMPORT_INIT_IMPORT',
    [WorkflowSteps.CSV_IMPORT_INIT_IMPORT_ERROR]: 'CSV_SUPPLIER_IMPORT_INIT_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT]: 'CSV_SUPPLIER_IMPORT_START_IMPORT',
    [WorkflowSteps.CSV_IMPORT_START_IMPORT_ERROR]: 'CSV_SUPPLIER_IMPORT_START_IMPORT_ERROR',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY]: 'CSV_SUPPLIER_IMPORT_GET_SUMMARY',
    [WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR]: 'CSV_SUPPLIER_IMPORT_GET_SUMMARY_ERROR',
  },
  [Workflows.EDIT_INVENTORY_SYNC_FLOW]: {
    [WorkflowSteps.START]: 'INVENTORY_SYNC_EDIT_START',
    [WorkflowSteps.COMPLETE]: 'INVENTORY_SYNC_EDIT_COMPLETE',
    [WorkflowSteps.ERROR]: 'INVENTORY_SYNC_EDIT_ERROR',
    [WorkflowSteps.CANCEL]: 'INVENTORY_SYNC_EDIT_CANCEL',
  },
  [Workflows.LABEL_LAYOUT_SELECTION_FLOW]: {
    [WorkflowSteps.START]: 'LABEL_LAYOUT_SELECTION_START',
    [WorkflowSteps.COMPLETE]: 'LABEL_LAYOUT_SELECTION_COMPLETE',
    [WorkflowSteps.ERROR]: 'LABEL_LAYOUT_SELECTION_ERROR',
    [WorkflowSteps.CANCEL]: 'LABEL_LAYOUT_SELECTION_CANCEL',
  },
  [Workflows.CUSTOM_PACKAGE_FLOW]: {
    [WorkflowSteps.START]: 'CUSTOM_PACKAGE_START',
    [WorkflowSteps.COMPLETE]: 'CUSTOM_PACKAGE_COMPLETE',
    [WorkflowSteps.ERROR]: 'CUSTOM_PACKAGE_ERROR',
    [WorkflowSteps.CANCEL]: 'CUSTOM_PACKAGE_CANCEL',
  },
  [Workflows.TAG_MANAGEMENT_FLOW]: {
    [WorkflowSteps.START]: 'TAG_MANAGEMENT_START',
    [WorkflowSteps.COMPLETE]: 'TAG_MANAGEMENT_COMPLETE',
    [WorkflowSteps.ERROR]: 'TAG_MANAGEMENT_ERROR',
    [WorkflowSteps.CANCEL]: 'TAG_MANAGEMENT_CANCEL',
  },
  [Workflows.EDIT_INVENTORY_SYNC_OPTIONS_FLOW]: {
    [WorkflowSteps.START]: 'INVENTORY_SYNC_OPTIONS_EDIT_START',
    [WorkflowSteps.COMPLETE]: 'INVENTORY_SYNC_OPTIONS_EDIT_COMPLETE',
    [WorkflowSteps.ERROR]: 'INVENTORY_SYNC_OPTIONS_EDIT_ERROR',
    [WorkflowSteps.CANCEL]: 'INVENTORY_SYNC_OPTIONS_EDIT_CANCEL',
  },
  [Workflows.FTU_WIZARD_FLOW]: {
    [WorkflowSteps.START]: 'FTU_WIZARD_START',
    [WorkflowSteps.CONTINUE]: 'FTU_WIZARD_CONTINUE',
    [WorkflowSteps.FTU_WIZARD_BACK]: 'FTU_WIZARD_BACK',
    [WorkflowSteps.FTU_WIZARD_SKIP]: 'FTU_WIZARD_SKIP',
    [WorkflowSteps.CLICK]: 'FTU_WIZARD_CLICK',
    [WorkflowSteps.COMPLETE]: 'FTU_WIZARD_COMPLETE',
    [WorkflowSteps.CANCEL]: 'FTU_WIZARD_CANCEL',
    [WorkflowSteps.ERROR]: 'FTU_WIZARD_ERROR',
  },

  [Workflows.ADVANCED_SHIPPING_NOTICE_FLOW]: {
    [WorkflowSteps.START]: 'ADVANCED_SHIPPING_NOTICE_FLOW_START',
    [WorkflowSteps.CONTINUE]: 'ADVANCED_SHIPPING_NOTICE_FLOW_CONTINUE',
    [WorkflowSteps.CLOSE]: 'ADVANCED_SHIPPING_NOTICE_FLOW_CLOSE',
    [WorkflowSteps.COMPLETE]: 'ADVANCED_SHIPPING_NOTICE_FLOW_COMPLETE',
    [WorkflowSteps.ERROR]: 'ADVANCED_SHIPPING_NOTICE_FLOW_ERROR',
    [WorkflowSteps.SAVE_ASN_STEP]: 'ADVANCED_SHIPPING_NOTICE_SAVE_STEP',
    [WorkflowSteps.SUBMIT_FREIGHT_CONFIGURATION]: 'ADVANCED_SHIPPING_NOTICE_SUBMIT_FREIGHT_CONFIGURATION',
  },
  [Workflows.PICK_PACK_STATION]: {
    [WorkflowSteps.START]: 'PICK_PACK_STATION_START',
    [WorkflowSteps.CANCEL]: 'PICK_PACK_STATION_CANCEL',
    [WorkflowSteps.ITEM_VERIFIED_SCANNED]: 'PICK_PACK_STATION_ITEM_VERIFIED_SCANNED',
    [WorkflowSteps.ITEM_VERIFIED_MANUAL]: 'PICK_PACK_STATION_ITEM_VERIFIED_MANUAL',
    [WorkflowSteps.VERIFY_ALL]: 'PICK_PACK_STATION_VERIFY_ALL',
    [WorkflowSteps.CLEAR_ALL]: 'PICK_PACK_STATION_CLEAR_ALL',
    [WorkflowSteps.EDIT_VERIFICATION]: 'PICK_PACK_STATION_EDIT_VERIFICATION',
    [WorkflowSteps.COMPLETE]: 'PICK_PACK_STATION_COMPLETE',
    [WorkflowSteps.REPORT_ISSUE]: 'PICK_PACK_STATION_REPORT_ISSUE',
  },
  [Workflows.SCAN_TO]: {
    [WorkflowSteps.START]: 'START_SCAN_TO',
    [WorkflowSteps.CONTINUE]: 'SCAN_ITEM_VERIFIED',
    [WorkflowSteps.COMPLETE]: 'COMPLETE_SCAN_TO',
    [WorkflowSteps.CANCEL]: 'CANCEL_SCAN_TO',
    [WorkflowSteps.ITEM_VERIFIED_MANUAL]: 'SCAN_ITEM_VERIFIED_MANUAL',
    [WorkflowSteps.ITEM_VERIFIED_SCANNED]: 'SCAN_ITEM_VERIFIED_SCANNED',
    [WorkflowSteps.EDIT_VERIFICATION]: 'SCAN_EDIT_VERIFICATION',
    [WorkflowSteps.VERIFY_ALL]: 'SCAN_VERIFY_ALL',
    [WorkflowSteps.CLEAR_ALL]: 'SCAN_CLEAR_ALL',
  },
  [Workflows.SCAN_TO_RECEIVE_FLOW]: {
    [WorkflowSteps.START]: 'SCAN_TO_RECEIVE_START',
    [WorkflowSteps.COMPLETE]: 'SCAN_TO_RECEIVE_SUBMISSION',
    [WorkflowSteps.CANCEL]: 'SCAN_TO_RECEIVE_CANCEL',
    [WorkflowSteps.ERROR]: 'SCAN_TO_RECEIVE_SUBMISSION_ERROR',
    [WorkflowSteps.NOT_FOUND]: 'SCAN_TO_RECEIVE_NOT_FOUND',
    [WorkflowSteps.IS_BUNDLE]: 'SCAN_TO_RECEIVE_IS_BUNDLE',
    [WorkflowSteps.CLOSE]: 'SCAN_TO_RECEIVE_CLOSE',
  },
  [Workflows.PURCHASE_ORDER_RECEIVE_FLOW]: {
    [WorkflowSteps.NOT_FOUND]: 'PURCHASE_ORDER_RECEIVE_NOT_FOUND',
  },
  [Workflows.TRANSFER_ORDER_RECEIVE_FLOW]: {
    [WorkflowSteps.NOT_FOUND]: 'TRANSFER_ORDER_RECEIVE_NOT_FOUND',
  },
  [Workflows.CREATE_RETURN_FLOW]: {
    [WorkflowSteps.START]: 'CREATE_RETURN_START',
    [WorkflowSteps.ITEMS_SELECTED]: 'CREATE_RETURN_ITEMS_SELECTED',
    [WorkflowSteps.COMPLETE]: 'CREATE_RETURN_COMPLETE',
    [WorkflowSteps.ERROR]: 'CREATE_RETURN_ERROR',
    [WorkflowSteps.CANCEL]: 'CREATE_RETURN_CANCEL',
  },
  [Workflows.UPSELL_BILLING_PLAN_FLOW]: {
    [WorkflowSteps.START]: 'UPSELL_BILLING_PLAN_START',
    [WorkflowSteps.COMPLETE]: 'UPSELL_BILLING_PLAN_COMPLETE',
    [WorkflowSteps.CANCEL]: 'UPSELL_BILLING_PLAN_CANCEL',
  },
  [Workflows.PROMOTIONAL_PLAN_MIGRATION_FLOW]: {
    [WorkflowSteps.START]: 'PROMOTIONAL_PLAN_MIGRATION_START',
    [WorkflowSteps.COMPLETE]: 'PROMOTIONAL_PLAN_MIGRATION_COMPLETE',
    [WorkflowSteps.CANCEL]: 'PROMOTIONAL_PLAN_MIGRATION_CANCEL',
    [WorkflowSteps.ERROR]: 'PROMOTIONAL_PLAN_MIGRATION_ERROR',
    [WorkflowSteps.CONTINUE]: 'PROMOTIONAL_PLAN_MIGRATION_CONTINUE',
  },
  [Workflows.STORE_ERROR_RESOLUTION_FLOW]: {
    [WorkflowSteps.START]: 'STORE_ERROR_RESOLUTION_START',
    [WorkflowSteps.CLICK]: 'STORE_ERROR_RESOLUTION_RECONFIGURE_CLICKED',
    [WorkflowSteps.CONTACT_SUPPORT]: 'STORE_ERROR_RESOLUTION_CONTACT_SUPPORT_CLICKED',
    [WorkflowSteps.GET_HELP]: 'STORE_ERROR_RESOLUTION_GET_HELP_CLICKED',
    [WorkflowSteps.COMPLETE]: 'STORE_ERROR_RESOLUTION_COMPLETE',
    [WorkflowSteps.CANCEL]: 'STORE_ERROR_RESOLUTION_CANCEL',
    [WorkflowSteps.ERROR]: 'STORE_ERROR_RESOLUTION_ERROR',
    [WorkflowSteps.REDIRECT]: 'STORE_ERROR_RESOLUTION_REDIRECT',
  },
  [Workflows.FBS_APPLICATION_FLOW]: {
    [WorkflowSteps.START]: 'FBS_APPLICATION_START',
    [WorkflowSteps.CONTINUE]: 'FBS_APPLICATION_CONTINUE',
    [WorkflowSteps.COMPLETE]: 'FBS_APPLICATION_COMPLETE',
    [WorkflowSteps.ERROR]: 'FBS_APPLICATION_ERROR',
    [WorkflowSteps.CANCEL]: 'FBS_APPLICATION_CANCEL',
  },
  [Workflows.HARMONIZATION_SINGLE_FLOW]: {
    [WorkflowSteps.START]: 'HARMONIZATION_SINGLE_START',
    [WorkflowSteps.HARMONIZATION_CODE_SELECTED]: 'HARMONIZATION_SINGLE_SELECTED',
    [WorkflowSteps.COMPLETE]: 'HARMONIZATION_SINGLE_COMPLETE',
    [WorkflowSteps.CANCEL]: 'HARMONIZATION_SINGLE_CANCEL',
  },
  [Workflows.HARMONIZATION_BULK_FLOW]: {
    [WorkflowSteps.START]: 'HARMONIZATION_BULK_START',
    [WorkflowSteps.HARMONIZATION_CODE_SELECTED]: 'HARMONIZATION_BULK_SELECTED',
    [WorkflowSteps.HARMONIZATION_ACCEPT_HIGH_CONFIDENCE]: 'HARMONIZATION_BULK_ACCEPT_HIGH_CONFIDENCE',
    [WorkflowSteps.COMPLETE]: 'HARMONIZATION_BULK_COMPLETE',
    [WorkflowSteps.CANCEL]: 'HARMONIZATION_BULK_CANCEL',
  },
  [Workflows.HTML_EDITOR_FLOW]: {
    [WorkflowSteps.START]: 'HTML_EDITOR_START',
    [WorkflowSteps.CHANGE_SETTING]: 'HTML_EDITOR_CHANGE_SETTING',
    [WorkflowSteps.INSERT_FIELD]: 'HTML_EDITOR_INSERT_FIELD',
    [WorkflowSteps.FORMAT_HTML]: 'HTML_EDITOR_FORMAT_HTML',
    [WorkflowSteps.UNDO]: 'HTML_EDITOR_UNDO',
    [WorkflowSteps.REDO]: 'HTML_EDITOR_REDO',
    [WorkflowSteps.REFRESH_PREVIEW]: 'HTML_EDITOR_REFRESH_PREVIEW',
    [WorkflowSteps.ZOOM_IN]: 'HTML_EDITOR_ZOOM_IN',
    [WorkflowSteps.ZOOM_OUT]: 'HTML_EDITOR_ZOOM_OUT',
    [WorkflowSteps.OPEN_CHAT_ASSISTANT]: 'HTML_EDITOR_OPEN_CHAT_ASSISTANT',
    [WorkflowSteps.COMPLETE]: 'HTML_EDITOR_COMPLETE',
    [WorkflowSteps.CANCEL]: 'HTML_EDITOR_CANCEL',
    [WorkflowSteps.ERROR]: 'HTML_EDITOR_ERROR',
  },
};

export const useTrackWorkflow = (workflow) => {
  const { getWorkflowData, updateWorkflowData } = useContext(SegmentEventWorkflowTrackingContext);
  return useCallback(
    (workflowStep, metadata = {}) => {
      const shouldReset = WorkflowSteps.START === workflowStep;
      const workflowData = EventsThatUpdateWorkflowData.includes(workflowStep)
        ? updateWorkflowData(Workflows[workflow], metadata, shouldReset)
        : getWorkflowData(Workflows[workflow]);
      const fullMetadata = { ...workflowData, ...metadata };
      const segmentEvent = WorkFlowStepToSegmentEventMapping[workflow][workflowStep];
      Segment.trackEvent(segmentEvent, fullMetadata);
      Yofi.applySessionMetadataForWorkflow(workflow, segmentEvent);
      return fullMetadata;
    },
    [getWorkflowData, updateWorkflowData, workflow],
  );
};

export const trackWorkflow = (workflow, workflowStep, metadata = {}) => {
  const workflowData = getWorkflowState(Workflows[workflow]);
  const fullMetadata = { ...workflowData, ...metadata };
  const segmentEvent = WorkFlowStepToSegmentEventMapping[workflow][workflowStep];
  Segment.trackEvent(segmentEvent, fullMetadata);
  Yofi.applySessionMetadataForWorkflow(workflow, segmentEvent);
};

// If a complete event is fired from an epic by a pusher response all users for a seller
// will fire the complete event. isCurrentUser will check if the current user is the user from the
// pusher response and only fire the complete event for that user.
// You may need to add the username to the pusher response on the BE

export const isCurrentUser = (state, username) => {
  const user = Global.selectUserCurrentUserInfo(state);
  return username === user.username;
};
